import { chain, maxBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DomainGq } from '../../../../../graphql/projects/models/DomainGq';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import { getColumnDefaultSize } from './getColumnDefaultSize';
import { KeywordTableColumn, KeywordTableColumnTypes } from './models';

export type KeywordColumnPickerBag = ReturnType<typeof useKeywordColumnPicker>;

export function useKeywordColumnPicker(
  initialState: KeywordTableColumn[],
  domains?: DomainGq[],
  useAutomaticDomains?: boolean,
  tagGroups?: TagGroupGq[],
  hasTranslations?: boolean,
  hasEnglishTranslations?: boolean
) {

  const manualDomains: DomainGq[] = useMemo(() => domains?.filter((d) => d.type !== DomainTypeGq.AUTOMATIC), [domains]);
  const automaticDomains: DomainGq[] = useMemo(() => domains
    ?.filter((d) => useAutomaticDomains ? d.type === DomainTypeGq.AUTOMATIC : false)
    ?.filter(d => !manualDomains?.some(m => m.protocol + m.hostName === d.protocol + d.hostName))
    ,
    [domains, useAutomaticDomains]);

  const relevantDomains = useMemo(() => {
    if (!!manualDomains?.length || !!automaticDomains?.length) {
      return [...manualDomains, ...automaticDomains]
    } else return []
  }, [manualDomains, automaticDomains]);

  // Set up cleaned initial state
  // ... This is kind of complicated when ensuring that (1) there's no duplicates, (2) there's no invalid columns
  const cleanedInitialState = useMemo(() => {
    const columnTypes = Object.values(KeywordTableColumnTypes).filter(
      (columnType) => (columnType as any) > -1
    ) as any;

    // Step 1: Setup base hidden columns
    let result: KeywordTableColumn[] = columnTypes.filter(
      (type: any) =>
        type !== KeywordTableColumnTypes.Rank &&
        type !== KeywordTableColumnTypes.TagGroup &&
        (hasTranslations || type !== KeywordTableColumnTypes.TranslationFrom) &&
        (hasTranslations || type !== KeywordTableColumnTypes.TranslationFromVolume) &&
        (hasEnglishTranslations || type !== KeywordTableColumnTypes.EnglishTranslation)
    )
      .map(
        (type: any): KeywordTableColumn => ({
          type: type as KeywordTableColumnTypes,
          order: undefined,
          size: getColumnDefaultSize(type),
        })
      );

    // Step 2: Add columns from provided initial state
    chain(initialState)
      .filter(
        (column) =>
          // Only valid column types (in case we've added/removed types)
          columnTypes.includes(column.type) &&
          // Make sure only id is available on rank/tag-group since we used these for uniqueness
          (!column.id ||
            (column.type === KeywordTableColumnTypes.Rank && !!column.id) ||
            (column.type === KeywordTableColumnTypes.TagGroup && !!column.id))
      )
      // Only unique columns (to avoid double insertion)
      .uniqBy((column) => column.type.toString() + column.id)
      // Order the columns
      .orderBy([(column, index) => column.order, (column, index) => index])
      .forEach((column, index) => {
        // Remove old column if exists
        result = result.filter(
          (baseColumn) => baseColumn.type != column.type || baseColumn.id !== column.id
        );

        // Add new column (at the beginning)
        result.push({
          type: column.type,
          id: column.id,
          order: column.order !== undefined ? index - 1000 : undefined,
          url: column.url,
          traffic: column.traffic,
          size: column.size ?? getColumnDefaultSize(column.type),
          sizePx: column.sizePx,
        });
      })
      .value();

    // Step 3: Update result order value (in case things got a bit shuffled)
    result = chain(result)
      .orderBy([(column) => column.order])
      .map((column, index) => ({ ...column, order: column.order != undefined ? index : undefined }))
      .value();

    return result;
  }, []);

  // State
  const [columnState, setColumnState] = useState<KeywordTableColumn[]>(cleanedInitialState);

  // Add/remove tag groups and domains
  useEffect(() => {
    let nextColumnState: KeywordTableColumn[] = [...columnState];
    let stateChanged = false;

    // Check if a domain has been added
    relevantDomains?.forEach((domain) => {
      if (columnState.some((c) => c.type === KeywordTableColumnTypes.Rank && c.id === domain.id))
        return;
      nextColumnState.push({
        type: KeywordTableColumnTypes.Rank,
        id: domain.id,
        size: getColumnDefaultSize(KeywordTableColumnTypes.Rank),
      });

      stateChanged = true;
    });

    // Check if a tag group has been added
    tagGroups?.forEach((tagGroup) => {
      if (
        columnState.some((c) => c.type === KeywordTableColumnTypes.TagGroup && c.id === tagGroup.id)
      )
        return;
      nextColumnState.push({
        type: KeywordTableColumnTypes.TagGroup,
        id: tagGroup.id,
        size: getColumnDefaultSize(KeywordTableColumnTypes.TagGroup),
      });
      stateChanged = true;
    });

    nextColumnState = nextColumnState.filter((column) => {

      // Check if a domain has been removed
      if (column.type === KeywordTableColumnTypes.Rank && !relevantDomains?.some((d) => d.id === column.id)
      ) {
        stateChanged = true;
        return false;
      }

      // Check if a tag group has been removed
      if (
        column.type === KeywordTableColumnTypes.TagGroup &&
        !tagGroups?.some((tg) => tg.id === column.id)
      ) {
        stateChanged = true;
        return false;
      }

      // Otherwise it's a normal column - keep
      return true;
    });

    if (stateChanged) {
      setColumnState(nextColumnState);
    }
  }, [relevantDomains, tagGroups]);

  // Add/remove translations
  useEffect(() => {
    let nextColumnState: KeywordTableColumn[] = [...columnState];
    let stateChanged = false;

    // English translation
    if (
      hasEnglishTranslations &&
      !nextColumnState.some((c) => c.type === KeywordTableColumnTypes.EnglishTranslation)
    ) {
      stateChanged = true;
      nextColumnState.push({
        type: KeywordTableColumnTypes.EnglishTranslation,
        order: 15,
        size: getColumnDefaultSize(KeywordTableColumnTypes.EnglishTranslation),
      });
    }

    // Translation
    if (
      hasTranslations &&
      !nextColumnState.some((c) => c.type === KeywordTableColumnTypes.TranslationFrom)
    ) {
      stateChanged = true;
      nextColumnState.push({
        type: KeywordTableColumnTypes.TranslationFrom,
        order: 16,
        size: getColumnDefaultSize(KeywordTableColumnTypes.TranslationFrom),
      });
      nextColumnState.push({
        type: KeywordTableColumnTypes.TranslationFromVolume,
        order: 17,
        size: getColumnDefaultSize(KeywordTableColumnTypes.TranslationFromVolume),
      });
    }

    if (stateChanged) {
      setColumnState(nextColumnState);
    }
  }, [hasEnglishTranslations, hasTranslations]);

  const shownOrderedColumns = useMemo(
    () =>
      chain(columnState)
        .filter((c) => c.order !== undefined)
        .orderBy((c) => c.order)
        .value(),
    [columnState]
  );

  const shownDomains = useMemo(
    () =>
      relevantDomains?.filter((d) =>
        shownOrderedColumns.some(
          (soc) => soc.type === KeywordTableColumnTypes.Rank && soc.id === d.id
        )
      ),
    [relevantDomains, shownOrderedColumns]
  );

  const shownTagGroups = useMemo(
    () =>
      tagGroups?.filter((tg) =>
        shownOrderedColumns.some(
          (soc) => soc.type === KeywordTableColumnTypes.TagGroup && soc.id === tg.id
        )
      ),
    [tagGroups, shownOrderedColumns]
  );

  const toggleColumnType = useCallback((type: KeywordTableColumnTypes, show: boolean) => {
    setColumnState((prev) => {
      if (prev.some((c) => c.type === type && c.order !== undefined)) return prev;
      const oldMaxOrder = maxBy(prev, (v) => v.order)?.order ?? 0;
      let oneColumnFlipped = false;
      return prev.map((c) => {
        if (c.type !== type) return c;
        if (oneColumnFlipped) return c;
        oneColumnFlipped = true;
        return { ...c, order: oldMaxOrder + 1 };
      });
    });
  }, []);

  const showTagGroup = useCallback((tagGroupId?: number) => {
    if (!tagGroupId) return;

    setColumnState((prev) => {
      if (
        prev.some(
          (t) =>
            t.id === tagGroupId &&
            t.type === KeywordTableColumnTypes.TagGroup &&
            t.order !== undefined
        )
      )
        return prev;

      const oldMaxOrder = maxBy(prev, (v) => v.order)?.order ?? 0;
      let currentMaxOrder = oldMaxOrder;
      return prev.map((column) => {
        if (column.type === KeywordTableColumnTypes.TagGroup && column.id === tagGroupId)
          return { ...column, order: currentMaxOrder };
        return column;
      });
    });
  }, []);

  const showProjectDomain = useCallback(
    (domainId?: number) => {
      // Make sure to show some sites (either current selection or random selection)
      const projectDomainId = domainId ?? relevantDomains?.find((d) => d.type === DomainTypeGq.PROJECT);

      if (!projectDomainId) {
        console.error(`No client domain ID found, ${domainId}`);
      }

      setColumnState((prev) => {
        if (
          prev.some(
            (t) =>
              t.id === projectDomainId &&
              t.type === KeywordTableColumnTypes.Rank &&
              t.order !== undefined
          )
        )
          return prev;

        const oldMaxOrder = maxBy(prev, (v) => v.order)?.order ?? 0;
        let currentMaxOrder = oldMaxOrder;
        return prev.map((column) => {
          if (column.type === KeywordTableColumnTypes.Rank && column.id === projectDomainId)
            return { ...column, order: currentMaxOrder };
          return column;
        });
      });
    },
    [relevantDomains]
  );

  return useMemo(
    () => ({
      columnState,
      setColumnState,
      shownOrderedColumns,
      shownDomains,
      shownTagGroups,
      showTagGroup,
      showProjectDomain,
      toggleColumn: toggleColumnType,
    }),
    [columnState, shownDomains, shownTagGroups, showProjectDomain]
  );
}
